import { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Chip,
  Autocomplete,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useEmail } from '../../hooks/useEmail';
import SocketIOContext from '../../context/socketIOContext';
import MailDeliveryAnimation from './MailDeliveryAnimation';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TERMINAL_STATUSES = ['delivered', 'failed', 'bounced', 'skipped'];

const EMPTY_INIT = {
  fromEmail: '',
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  textBody: '',
  attachments: [],
};

const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getAttachmentBytes = att => Number(att?.size || att?.contentLength || 0) || 0;

// Split free-text entries on common separators so pasting "a@x.de, b@y.de"
// produces individual chips.
const splitRecipients = values =>
  values
    .flatMap(value => String(value).split(/[;,\s]+/))
    .map(value => value.trim())
    .filter(Boolean);

// De-duplicate a list of email addresses case-insensitively, preserving order.
const dedupeRecipients = arr => {
  const seen = new Set();
  const out = [];
  arr.forEach(entry => {
    const email = String(entry).trim();
    const key = email.toLowerCase();
    if (email && !seen.has(key)) {
      seen.add(key);
      out.push(email);
    }
  });
  return out;
};

// Merge any pending (typed but not yet committed) input into the committed list.
const mergePending = (list, input) =>
  dedupeRecipients([...list, ...splitRecipients(input ? [input] : [])]);

export default function ComposeEmailModal({ open, onClose, onSent, canSend = true }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {
    getSenderIdentities,
    composeEmail,
    getComposeDrafts,
    getComposeDraft,
    saveComposeDraft,
    deleteComposeDraft,
    getMailDeliveryStatus,
  } = useEmail();
  const { emit: socketEmit, on: socketOn, isConnected } = useContext(SocketIOContext) || {};

  // ----- form state -----
  const [formKey, setFormKey] = useState(0); // bump to remount uncontrolled inputs
  const [formInit, setFormInit] = useState(EMPTY_INIT);
  const [fromEmail, setFromEmail] = useState('');
  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [bccList, setBccList] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);
  const dirtyRef = useRef(false);
  const [draftId, setDraftId] = useState(null);

  // ----- identities -----
  const [identities, setIdentities] = useState([]);
  const [identitiesLoading, setIdentitiesLoading] = useState(false);

  // ----- drafts -----
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // ----- send / delivery -----
  const [step, setStep] = useState('compose'); // 'compose' | 'delivery'
  const [sending, setSending] = useState(false);
  const [sentMailId, setSentMailId] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  const resetForm = useCallback((init = EMPTY_INIT, newDraftId = null) => {
    setFormInit(init);
    setFromEmail(init.fromEmail || '');
    setToList(init.to || []);
    setCcList(init.cc || []);
    setBccList(init.bcc || []);
    setToInput('');
    setCcInput('');
    setBccInput('');
    setShowCcBcc((init.cc?.length || 0) > 0 || (init.bcc?.length || 0) > 0);
    setAttachments(init.attachments || []);
    setDraftId(newDraftId);
    setFormKey(key => key + 1);
    dirtyRef.current = false;
  }, []);

  // Load identities + drafts when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setIdentitiesLoading(true);
    getSenderIdentities()
      .then(data => {
        if (cancelled) return;
        const list = data?.identities || [];
        setIdentities(list);
        // Preselect the first identity if none chosen yet.
        setFromEmail(prev => prev || (list[0] ? list[0].email : ''));
      })
      .catch(() => {
        if (!cancelled) toast.error('Fehler beim Laden der Absender-Identitäten');
      })
      .finally(() => {
        if (!cancelled) setIdentitiesLoading(false);
      });

    refreshDrafts();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refreshDrafts = useCallback(() => {
    setDraftsLoading(true);
    getComposeDrafts()
      .then(data => setDrafts(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setDraftsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedIdentity = useMemo(
    () => identities.find(identity => identity.email === fromEmail) || null,
    [identities, fromEmail]
  );

  const buildPayload = useCallback(() => {
    return {
      fromEmail,
      fromName: selectedIdentity?.name || '',
      // Include addresses typed but not yet committed to a chip.
      to: mergePending(toList, toInput),
      cc: mergePending(ccList, ccInput),
      bcc: mergePending(bccList, bccInput),
      subject: subjectRef.current?.value || '',
      textBody: bodyRef.current?.value || '',
      attachments: attachments.map(att => ({
        name: att.name,
        contentType: att.contentType,
        content: att.content,
      })),
    };
  }, [fromEmail, selectedIdentity, toList, ccList, bccList, toInput, ccInput, bccInput, attachments]);

  const hasContent = useCallback(() => {
    return Boolean(
      (subjectRef.current?.value || '').trim() ||
        (bodyRef.current?.value || '').trim() ||
        toList.length ||
        ccList.length ||
        bccList.length ||
        toInput.trim() ||
        ccInput.trim() ||
        bccInput.trim() ||
        attachments.length
    );
  }, [toList, ccList, bccList, toInput, ccInput, bccInput, attachments]);

  // ----- draft auto-save (every 5s while dirty) -----
  const persistDraft = useCallback(
    async ({ silent = true } = {}) => {
      if (!hasContent()) return null;
      try {
        if (!silent) setSavingDraft(true);
        const saved = await saveComposeDraft(buildPayload(), draftId);
        if (saved?._id) setDraftId(saved._id);
        dirtyRef.current = false;
        if (!silent) {
          toast.success('Entwurf gespeichert');
          refreshDrafts();
        }
        return saved;
      } catch (error) {
        if (!silent) toast.error('Fehler beim Speichern des Entwurfs');
        return null;
      } finally {
        if (!silent) setSavingDraft(false);
      }
    },
    [hasContent, saveComposeDraft, buildPayload, draftId, refreshDrafts]
  );

  useEffect(() => {
    if (!open || step !== 'compose') return undefined;
    const interval = setInterval(() => {
      if (dirtyRef.current) persistDraft({ silent: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [open, step, persistDraft]);

  // ----- attachments -----
  const handleFileSelect = async event => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const newAttachments = [];
    let total = attachments.reduce((sum, att) => sum + getAttachmentBytes(att), 0);

    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`Die Datei "${file.name}" ist zu groß (max. 10MB)`);
        continue;
      }
      if (total + file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`Die Anhänge sind insgesamt zu groß (max. ${formatFileSize(MAX_ATTACHMENT_BYTES)})`);
        continue;
      }
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        newAttachments.push({
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          content: base64,
          size: file.size,
        });
        total += file.size;
      } catch (error) {
        toast.error(`Fehler beim Lesen der Datei "${file.name}"`);
      }
    }

    if (newAttachments.length) {
      setAttachments(prev => [...prev, ...newAttachments]);
      markDirty();
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    markDirty();
  };

  // ----- recipient chip handlers -----
  // Returns the full set of Autocomplete props for a recipient field, handling
  // chips, paste/separator splitting, and committing pending input on blur so a
  // typed-but-not-Entered address is never silently dropped.
  const recipientFieldProps = (list, setList, input, setInput) => ({
    multiple: true,
    freeSolo: true,
    options: [],
    value: list,
    inputValue: input,
    onChange: (_event, values) => {
      setList(dedupeRecipients(splitRecipients(values)));
      markDirty();
    },
    onInputChange: (_event, value, reason) => {
      if (reason === 'reset') {
        setInput('');
        return;
      }
      if (/[\s,;]/.test(value)) {
        // A separator was typed/pasted: commit completed tokens, keep the rest.
        const parts = value.split(/[\s,;]+/);
        const remainder = parts.pop();
        const tokens = parts.filter(Boolean);
        if (tokens.length) {
          setList(dedupeRecipients([...list, ...tokens]));
          markDirty();
        }
        setInput(remainder || '');
      } else {
        setInput(value);
      }
    },
    onBlur: () => {
      if (input.trim()) {
        setList(dedupeRecipients([...list, ...splitRecipients([input])]));
        setInput('');
        markDirty();
      }
    },
    renderTags: (value, getTagProps) =>
      value.map((option, index) => {
        const invalid = !EMAIL_REGEX.test(option);
        return (
          <Chip
            key={option + index}
            label={option}
            size='small'
            color={invalid ? 'error' : 'default'}
            variant={invalid ? 'outlined' : 'filled'}
            {...getTagProps({ index })}
          />
        );
      }),
  });

  // Live warning is based on committed chips only (onBlur commits pending input),
  // so it does not flash while the user is mid-typing an address.
  const invalidRecipients = useMemo(
    () => [...toList, ...ccList, ...bccList].filter(email => !EMAIL_REGEX.test(email)),
    [toList, ccList, bccList]
  );

  // ----- drafts panel -----
  const handleOpenDraft = async draft => {
    try {
      const full = await getComposeDraft(draft._id);
      resetForm(
        {
          fromEmail: full.fromEmail || '',
          to: full.to || [],
          cc: full.cc || [],
          bcc: full.bcc || [],
          subject: full.subject || '',
          textBody: full.textBody || '',
          attachments: (full.attachments || []).map(att => ({
            name: att.name,
            contentType: att.contentType,
            content: att.content,
            size: att.contentLength,
          })),
        },
        full._id
      );
      if (full.fromEmail) setFromEmail(full.fromEmail);
      setShowDrafts(false);
    } catch (error) {
      toast.error('Fehler beim Laden des Entwurfs');
    }
  };

  const handleDeleteDraft = async (event, draft) => {
    event.stopPropagation();
    try {
      await deleteComposeDraft(draft._id);
      setDrafts(prev => prev.filter(item => item._id !== draft._id));
      if (draftId === draft._id) setDraftId(null);
      toast.success('Entwurf gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen des Entwurfs');
    }
  };

  // ----- send -----
  const validateBeforeSend = () => {
    if (!fromEmail) {
      toast.error('Bitte eine Absender-Identität wählen');
      return false;
    }
    const effectiveTo = mergePending(toList, toInput);
    if (effectiveTo.length === 0) {
      toast.error('Bitte mindestens einen Empfänger angeben');
      return false;
    }
    const allRecipients = [
      ...effectiveTo,
      ...mergePending(ccList, ccInput),
      ...mergePending(bccList, bccInput),
    ];
    if (allRecipients.some(email => !EMAIL_REGEX.test(email))) {
      toast.error('Bitte ungültige E-Mail-Adressen korrigieren');
      return false;
    }
    if (!(subjectRef.current?.value || '').trim()) {
      toast.error('Bitte einen Betreff angeben');
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    if (!validateBeforeSend()) return;
    setSending(true);
    // Capture current text into formInit so the form can be restored (with the
    // typed subject/body) if the user returns from the delivery step to fix a
    // failed send. Recipients/attachments/from are already in state.
    setFormInit(prev => ({
      ...prev,
      subject: subjectRef.current?.value || '',
      textBody: bodyRef.current?.value || '',
    }));
    try {
      const response = await composeEmail({ ...buildPayload(), draftId });
      const mailId = response?.mailId || response?._id;
      if (draftId) {
        setDrafts(prev => prev.filter(item => item._id !== draftId));
        setDraftId(null);
      }
      setSentMailId(mailId);
      setDeliveryStatus({ status: 'pending', recipient: response?.to || toList.join(', ') });
      setPollCount(0);
      setStep('delivery');
      if (onSent) onSent();
    } catch (error) {
      toast.error(error?.message || 'Fehler beim Versenden der E-Mail');
    } finally {
      setSending(false);
    }
  };

  // ----- delivery feedback: socket join -----
  useEffect(() => {
    if (step !== 'delivery' || !sentMailId || typeof socketEmit !== 'function') return undefined;
    socketEmit('join_mail_delivery_status', { mailId: sentMailId });
    return undefined;
  }, [step, sentMailId, socketEmit, isConnected]);

  // ----- delivery feedback: socket listener -----
  useEffect(() => {
    if (step !== 'delivery' || !sentMailId || typeof socketOn !== 'function') return undefined;
    const unsubscribe = socketOn('mail:deliveryStatus', payload => {
      if (!payload || String(payload.mailId) !== String(sentMailId)) return;
      setDeliveryStatus(current => ({ ...(current || {}), ...payload }));
    });
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [step, sentMailId, socketOn]);

  // ----- delivery feedback: polling fallback -----
  useEffect(() => {
    if (step !== 'delivery' || !sentMailId) return undefined;
    const status = deliveryStatus?.status;
    if (TERMINAL_STATUSES.includes(status)) return undefined;

    let cancelled = false;
    const delay = pollCount === 0 ? 400 : Math.min(6500, 650 + pollCount * 520);
    const timeout = setTimeout(async () => {
      try {
        const response = await getMailDeliveryStatus(sentMailId);
        if (!cancelled) {
          setDeliveryStatus(current => ({ ...(current || {}), ...response }));
          setPollCount(count => count + 1);
        }
      } catch (error) {
        if (!cancelled) setPollCount(count => count + 1);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [step, sentMailId, deliveryStatus?.status, pollCount, getMailDeliveryStatus]);

  const getDeliveryCopy = () => {
    const status = deliveryStatus?.status || 'pending';
    if (status === 'delivered') {
      return {
        title: 'E-Mail wurde zugestellt',
        text: 'Die E-Mail wurde erfolgreich an den Empfänger zugestellt.',
      };
    }
    if (status === 'failed' || status === 'bounced') {
      return {
        title: 'E-Mail wurde nicht zugestellt',
        text:
          deliveryStatus?.mail?.errorMessage ||
          'Die Adresse konnte nicht erreicht werden. Bitte Empfänger prüfen.',
      };
    }
    return {
      title: 'E-Mail wird zugestellt',
      text: 'Die E-Mail wurde versendet. Wir warten auf die Bestätigung der Zustellung.',
    };
  };

  // ----- close handling -----
  const handleClose = async ({ saveDraft = true } = {}) => {
    if (step === 'compose' && saveDraft && dirtyRef.current && hasContent()) {
      await persistDraft({ silent: true });
    }
    doReset();
    if (onClose) onClose();
  };

  const doReset = () => {
    resetForm(EMPTY_INIT, null);
    setStep('compose');
    setSentMailId(null);
    setDeliveryStatus(null);
    setPollCount(0);
    setShowDrafts(false);
  };

  const handleComposeAnother = () => {
    resetForm({ ...EMPTY_INIT, fromEmail }, null);
    setStep('compose');
    setSentMailId(null);
    setDeliveryStatus(null);
    setPollCount(0);
    refreshDrafts();
  };

  const deliveryStatusValue = deliveryStatus?.status || 'pending';

  return (
    <Dialog
      open={open}
      onClose={() => handleClose({ saveDraft: true })}
      fullScreen={fullScreen}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { height: fullScreen ? '100%' : '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        {step === 'compose' && showDrafts && (
          <IconButton edge='start' onClick={() => setShowDrafts(false)} aria-label='Zurück'>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography variant='h6' sx={{ flexGrow: 1, fontWeight: 700 }}>
          {step === 'delivery'
            ? 'E-Mail wird versendet'
            : showDrafts
              ? 'Entwürfe'
              : 'Neue E-Mail'}
        </Typography>
        {step === 'compose' && !showDrafts && (
          <Tooltip title='Entwürfe'>
            <IconButton
              onClick={() => {
                refreshDrafts();
                setShowDrafts(true);
              }}
              aria-label='Entwürfe'
            >
              <DraftsOutlinedIcon />
              {drafts.length > 0 && (
                <Box
                  component='span'
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: '999px',
                    fontSize: 10,
                    lineHeight: '16px',
                    minWidth: 16,
                    height: 16,
                    px: 0.5,
                    textAlign: 'center',
                  }}
                >
                  {drafts.length}
                </Box>
              )}
            </IconButton>
          </Tooltip>
        )}
        <IconButton onClick={() => handleClose({ saveDraft: true })} aria-label='Schließen'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
        {/* ---------- Drafts list ---------- */}
        {step === 'compose' && showDrafts && (
          <Box>
            {draftsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : drafts.length === 0 ? (
              <Typography color='text.secondary' sx={{ py: 4, textAlign: 'center' }}>
                Keine gespeicherten Entwürfe.
              </Typography>
            ) : (
              <List disablePadding>
                {drafts.map(draft => (
                  <ListItemButton
                    key={draft._id}
                    onClick={() => handleOpenDraft(draft)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                  >
                    <EditOutlinedIcon sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText
                      primary={draft.subject || '(Kein Betreff)'}
                      secondary={
                        `${(draft.to || []).join(', ') || 'Keine Empfänger'}` +
                        (draft.meta?.updatedAt
                          ? ` · ${dayjs(draft.meta.updatedAt).format('DD.MM.YYYY HH:mm')}`
                          : '')
                      }
                      primaryTypographyProps={{ noWrap: true, fontWeight: 600 }}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                    <Tooltip title='Entwurf löschen'>
                      <IconButton
                        edge='end'
                        size='small'
                        onClick={event => handleDeleteDraft(event, draft)}
                        aria-label='Entwurf löschen'
                      >
                        <DeleteOutlineIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        )}

        {/* ---------- Compose form ---------- */}
        {step === 'compose' && !showDrafts && (
          <Stack spacing={2.5} key={formKey}>
            <TextField
              select
              label='Absender'
              value={fromEmail}
              onChange={event => {
                setFromEmail(event.target.value);
                markDirty();
              }}
              fullWidth
              size='small'
              disabled={identitiesLoading}
              helperText={
                identitiesLoading
                  ? 'Identitäten werden geladen …'
                  : identities.length === 0
                    ? 'Keine Absender-Identitäten verfügbar'
                    : 'Versendende Identität auswählen'
              }
              InputProps={
                identitiesLoading
                  ? { endAdornment: <CircularProgress size={18} sx={{ mr: 3 }} /> }
                  : undefined
              }
            >
              {identities.map(identity => (
                <MenuItem key={identity.email} value={identity.email}>
                  {identity.name ? `${identity.name} <${identity.email}>` : identity.email}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Autocomplete
                {...recipientFieldProps(toList, setToList, toInput, setToInput)}
                renderInput={params => (
                  <TextField {...params} label='An' placeholder='E-Mail-Adresse eingeben' size='small' />
                )}
              />
              {!showCcBcc && (
                <Button
                  size='small'
                  onClick={() => setShowCcBcc(true)}
                  sx={{ mt: 0.5, textTransform: 'none' }}
                >
                  Cc/Bcc hinzufügen
                </Button>
              )}
            </Box>

            {showCcBcc && (
              <>
                <Autocomplete
                  {...recipientFieldProps(ccList, setCcList, ccInput, setCcInput)}
                  renderInput={params => (
                    <TextField {...params} label='Cc' placeholder='E-Mail-Adresse eingeben' size='small' />
                  )}
                />
                <Autocomplete
                  {...recipientFieldProps(bccList, setBccList, bccInput, setBccInput)}
                  renderInput={params => (
                    <TextField {...params} label='Bcc' placeholder='E-Mail-Adresse eingeben' size='small' />
                  )}
                />
              </>
            )}

            {invalidRecipients.length > 0 && (
              <Alert severity='warning'>
                Ungültige E-Mail-Adresse(n): {invalidRecipients.join(', ')}
              </Alert>
            )}

            <TextField
              label='Betreff'
              inputRef={subjectRef}
              defaultValue={formInit.subject}
              onChange={markDirty}
              fullWidth
              size='small'
            />

            <TextField
              label='Nachricht'
              inputRef={bodyRef}
              defaultValue={formInit.textBody}
              onChange={markDirty}
              fullWidth
              multiline
              minRows={fullScreen ? 6 : 10}
            />

            {/* Attachments */}
            <Box>
              <input
                type='file'
                multiple
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                variant='outlined'
                size='small'
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: 'none' }}
              >
                Anhang hinzufügen
              </Button>
              {attachments.length > 0 && (
                <Stack direction='row' spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {attachments.map((att, index) => (
                    <Chip
                      key={att.name + index}
                      label={`${att.name}${
                        getAttachmentBytes(att) ? ` (${formatFileSize(getAttachmentBytes(att))})` : ''
                      }`}
                      onDelete={() => handleRemoveAttachment(index)}
                      size='small'
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}

        {/* ---------- Delivery feedback ---------- */}
        {step === 'delivery' && (
          <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center', py: { xs: 2, md: 4 } }}>
            <MailDeliveryAnimation status={deliveryStatusValue} />
            <Box>
              <Typography variant='h5' sx={{ fontWeight: 700, mb: 1 }}>
                {getDeliveryCopy().title}
              </Typography>
              <Typography color='text.secondary' sx={{ maxWidth: 600 }}>
                {getDeliveryCopy().text}
              </Typography>
            </Box>
            {deliveryStatus?.recipient && (
              <Typography variant='body2' color='text.secondary'>
                Empfänger: <strong>{deliveryStatus.recipient}</strong>
              </Typography>
            )}
            {deliveryStatusValue === 'pending' && (
              <Alert severity='info' sx={{ width: '100%', maxWidth: 560, textAlign: 'left' }}>
                Die E-Mail wurde versendet. Der Status aktualisiert sich automatisch, sobald die
                Zustellung bestätigt wurde oder fehlschlägt.
              </Alert>
            )}
            {deliveryStatusValue === 'pending' && pollCount >= 22 && (
              <Alert severity='warning' sx={{ width: '100%', maxWidth: 560, textAlign: 'left' }}>
                Die Rückmeldung dauert ungewöhnlich lange. Der Abgleich läuft im Hintergrund weiter –
                du kannst dieses Fenster schließen.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, gap: 1, flexWrap: 'wrap' }}>
        {step === 'compose' && !showDrafts && (
          <>
            <Button
              onClick={() => persistDraft({ silent: false })}
              disabled={savingDraft || !hasContent()}
              startIcon={savingDraft ? <CircularProgress size={16} /> : <SaveOutlinedIcon />}
              sx={{ textTransform: 'none' }}
            >
              Als Entwurf speichern
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={() => handleClose({ saveDraft: true })} sx={{ textTransform: 'none' }}>
              Schließen
            </Button>
            <Tooltip title={!canSend ? 'Keine Berechtigung zum Versenden' : ''}>
              <span>
                <Button
                  variant='contained'
                  onClick={handleSend}
                  disabled={sending || !canSend}
                  startIcon={sending ? <CircularProgress size={16} color='inherit' /> : <SendIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Senden
                </Button>
              </span>
            </Tooltip>
          </>
        )}

        {step === 'compose' && showDrafts && (
          <Button onClick={() => setShowDrafts(false)} sx={{ textTransform: 'none' }}>
            Zurück
          </Button>
        )}

        {step === 'delivery' && (
          <>
            {(deliveryStatusValue === 'failed' || deliveryStatusValue === 'bounced') && (
              <Button
                onClick={() => {
                  // Recover the sent content back into the form to fix and resend.
                  setStep('compose');
                }}
                sx={{ textTransform: 'none' }}
              >
                Zurück zur Bearbeitung
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button onClick={() => handleComposeAnother()} sx={{ textTransform: 'none' }}>
              Weitere E-Mail
            </Button>
            <Button
              variant='contained'
              onClick={() => handleClose({ saveDraft: false })}
              sx={{ textTransform: 'none' }}
            >
              Fertig
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
